import * as React from 'react';
import { useAiFilter } from '@/scopes/search/search/domain/hooks';
import { useIntl } from '@/intl';
import { css, styled } from 'styled-components';
import { BaseInput, Button, SpaceBetween, tk, Typography } from '@sunwebgroup/ui-components';
import { useFeatureFlag } from '@/scopes/common/infrastructure/feature-flags';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${tk('spacing_lg')};

  > * {
    margin-bottom: ${tk('spacing_md')};

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const TextArea = styled(BaseInput.Box)<{ $disabled: boolean }>`
  resize: none;
  width: 100%;
  height: 120px;
  cursor: text;

  ${({ $disabled }) =>
    $disabled &&
    css`
      color: ${tk('color_semantic_text_disabled')};
      border-color: ${tk('color_semantic_border_disabled')};
      background: ${tk('color_semantic_bg_disabled')};
      pointer-events: none;
    `}
`;

export const IntelligentFilter = () => {
  const intl = useIntl();
  const { setUserQuery, isFetching, isAIError } = useAiFilter();
  const [value, setValue] = React.useState('');

  const showAIFilter = useFeatureFlag('ENABLE_AI_FILTER');

  const changeHandler = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value);
  };

  const submitHandler = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setUserQuery(value);
  };

  const keyPressedHandler = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      setUserQuery(value);
    }
  };

  return (
    showAIFilter && (
      <Wrapper>
        <Typography $variant="body-lg-bold">{intl.formatMessage({ id: 'search_ai_filter_label' })}</Typography>
        <Form onSubmit={submitHandler}>
          <SpaceBetween direction="y" spacing={tk('spacing_md')}>
            {isAIError && (
              <Typography $variant="body-sm">{intl.formatMessage({ id: 'search_ai_filter_error' })}</Typography>
            )}
            <BaseInput.Root>
              <TextArea
                as="textarea"
                value={value}
                onChange={changeHandler}
                placeholder={intl.formatMessage({ id: 'search_ai_filter_placeholder' })}
                onKeyDown={keyPressedHandler}
                $disabled={isFetching}
              />
            </BaseInput.Root>
            <Button type="submit" $withoutColor $hierarchy="secondary" disabled={isFetching}>
              {intl.formatMessage({ id: 'search_ai_filter_submit_text' })}
            </Button>
          </SpaceBetween>
        </Form>
      </Wrapper>
    )
  );
};
